import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Card, Spinner, Table } from 'react-bootstrap';
import { logger } from '@qlean/front-logger';
import { uniqBy } from 'lodash';
import { InView } from 'react-intersection-observer';
import { plainToClass } from 'class-transformer';

import { FindAvailableRescheduledCommunicationsResponseDto, RescheduledListCommunicationPreviewDto } from 'src/store/CommunicationStore';
import { PlatformCRMWeb } from 'src/services/GrpcService/codegen/app_pb';
import { parseDate } from 'src/utils/formatters';
import GrpcService from '../../services/GrpcService';
import { dictionary } from '../../utils/dictionary';

const infiniteScrollInitialPageSize = 30;
const infiniteScrollFollowingPageSize = 15;
const infiniteScrollBufferSize = 2;

export const ManagerCommunications = () => {
  const [managerCommunications, setManagerCommunications] = useState<RescheduledListCommunicationPreviewDto[]>([]);
  const [isLoadingManagerCommunications, setIsLoadingManagerCommunications] = useState(false);
  const [{ skip, take }, setPagination] = useState<PlatformCRMWeb.IPaginationOptions>({
    take: infiniteScrollInitialPageSize,
    skip: 0,
  });
  const tableBodyElRef = useRef<HTMLTableSectionElement | null>(null);
  const hasNextRef = useRef<boolean | null>(null);

  const findMoreManagerCommunications = async (
    args: PlatformCRMWeb.IFindAvailableRescheduledCommunicationsRequest
  ): Promise<boolean> => {
    if (isLoadingManagerCommunications) {
      return false;
    }

    setIsLoadingManagerCommunications(true);
    logger.debug(`[ManagerCommunications.findMoreManagerCommunications] run`, args);
    try {
      const result = await GrpcService.PlatformCRMWeb.CommunicationService.FindAvailableRescheduledCommunications(
        args
      );
      logger.debug(`[ManagerCommunications.findMoreManagerCommunications] result`, result);
      const { data, hasNext } = plainToClass(FindAvailableRescheduledCommunicationsResponseDto, result);

      setIsLoadingManagerCommunications(false);
      setManagerCommunications((existed) => uniqBy([...existed, ...data], (item) => item.id));
      hasNextRef.current = hasNext;

      return true;
    } catch (err) {
      logger.error(`[ManagerCommunications.findMoreManagerCommunications] error`, err);
      setIsLoadingManagerCommunications(false);
    }
    return false;
  };

  useEffect(() => {
    findMoreManagerCommunications({
      pagination: {
        take,
        skip,
      },
    } as PlatformCRMWeb.IFindAvailableRescheduledCommunicationsRequest);
  }, [skip, take]);

  const TableRow = ({ isObserved, children }: PropsWithChildren<{ isObserved: boolean }>) =>
    isObserved ? (
      <InView
        as="tr"
        onChange={(inView) => {
          const canLoadMore = hasNextRef.current !== false;

          if (inView && canLoadMore) {
            setPagination({
              skip: managerCommunications.length,
              take: infiniteScrollFollowingPageSize,
            });
          }
        }}
      >
        {children}
      </InView>
    ) : (
      <tr>{children}</tr>
    );

  return (
    <Card
      style={{ maxHeight: '100px', minHeight: 'calc(100% - 30px)', overflowY: 'auto' }}
      className="m-4 h-100"
    >
      <>
        {isLoadingManagerCommunications && (
          <div className="d-flex justify-content-center h-100 align-items-center">
            <Spinner animation="border" />
          </div>
        )}

        <Table>
          <thead>
            <tr>
              <th>Продукт</th>
              <th>Комментарий</th>
              <th>Дата отправления на перезвон</th>
              <th>Дата перезвона</th>
            </tr>
          </thead>
          <tbody ref={tableBodyElRef}>
            {managerCommunications &&
              managerCommunications.map((communication, i, array) => {
                const { id, activeTaskProductSlug, activeTaskComment: comment, postponedTo, updatedAt } = communication;
                const product = activeTaskProductSlug?.split('-');
              const productText =
                dictionary.selectOptions.unionServiceTypes.find((pr) => pr.id === product?.[0])?.value ||
                'Ручной лид';
                const observedIndex = array.length - infiniteScrollBufferSize;

                return (
                  <TableRow key={id} isObserved={i === observedIndex}>
                    <th>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/communication/${id}`}
                      >
                        {productText}
                      </a>
                    </th>
                    <td>{comment}</td>
                    <td>{parseDate(updatedAt)}</td>
                    <td>{parseDate(postponedTo)}</td>
                  </TableRow>
                );
              })}
          </tbody>
        </Table>
      </>
    </Card>
  );
};
